import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { User } from '@auth0/auth0-angular';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { lastValueFrom } from 'rxjs';
import { unitsLib } from 'src/app/core/services/unit-library';
import { OperationType, ShutInOperation } from 'src/app/perical/models/thermal-operation.model';
import { GetMudHydrostaticPressure } from 'src/app/perivis/shared/helpers/mud-hydrostatic.helper';
import { GetShortLengthValueFromInches, GetValueFromPpg } from 'src/app/perivis/shared/helpers/units.helper';
import { TrajectoryService } from 'src/app/shared/services/trajectory.service';

export interface ShutInOperationForm {
  type: OperationType;
  wellheadPressure: number;
  includeRiser: boolean;
  outsideDiameter: number;
  insideDiameter: number;
  pressureLocation: string;
  annulusIndex: number;
}

@Component({
    selector: 'app-shut-in-operation-details',
    templateUrl: './shut-in-operation-details.component.html',
    providers: subformComponentProviders(ShutInOperationDetailsComponent),
    standalone: false
})
export class ShutInOperationDetailsComponent implements OnInit, AfterViewInit, OnChanges {

  constructor(private _trajectoryService: TrajectoryService) {}

  public isLoading: boolean;
  public pressureUnit: string;
  public pressureValidation: { min: number, max: number };
  public showRiser: boolean;
  public selectedLandingString: string;
  public landingStringMissing: boolean;
  public shortLengthUnit: string;
  public isInside: boolean;

  @Output()
  selectedTabChange = new EventEmitter<string>();

  @Input()
  public isReadOnly: boolean;

  @Input()
  public currentString: any;

  @Input()
  public user: User;

  @Input()
  public defaultHangerPressure: number;

  @Input()
  public isAnnulus: boolean;

  @Input()
  public currentTubularIndex: number;

  @Input()
  public isSubsea: boolean;

  @Input()
  public defaultRiser: any;

  @Input()
  public drillString: any;

  @Input()
  public showLandingString: boolean;

  public pressureLocations = [
    { name: 'Wellhead', value: 'wellhead' },
    { name: 'Perforations', value: 'perforation' }
  ];

  public form = createForm<ShutInOperation, ShutInOperationForm>(this, {
    formType: FormType.SUB,
    formControls: {
      type: new UntypedFormControl(null),
      wellheadPressure: new UntypedFormControl(null, { updateOn: 'blur' }),
      pressureLocation: new UntypedFormControl('wellhead', [Validators.required]),
      outsideDiameter: new UntypedFormControl(null, { updateOn: 'blur' }),
      insideDiameter: new UntypedFormControl(null, { updateOn: 'blur' }),
      includeRiser: new UntypedFormControl(false),
      annulusIndex: new UntypedFormControl(null)
    }, toFormGroup: (obj: ShutInOperation | null): ShutInOperationForm => {
      if (!obj) {
        return null;
      }

      const { riser, includeRiser } = obj;

      this.showRiser = obj.annulusIndex == 0 &&  includeRiser;
      this.isInside = obj.annulusIndex == 0;

      return {
        type: obj.type,
        wellheadPressure: obj.wellheadPressure,
        pressureLocation: obj.pressureLocation,
        includeRiser: obj.includeRiser,
        outsideDiameter: riser?.outsideDiameter,
        insideDiameter: riser?.insideDiameter,
        annulusIndex: obj.annulusIndex
      };
    },
    fromFormGroup: (formValue: ShutInOperationForm): ShutInOperation => {
      const { outsideDiameter, insideDiameter, includeRiser } = formValue;

      return {
        type: OperationType.SHUTIN,
        wellheadPressure: formValue.wellheadPressure,
        pressureLocation: formValue.pressureLocation,
        includeRiser,
        riser: this.showRiser ? {
          outsideDiameter,
          insideDiameter
        } : null,
        annulusIndex: formValue.annulusIndex
      };
    }
  });

  async ngOnInit(): Promise<void> {
    this.pressureUnit = unitsLib[this.user.units.pressure].symbol;
    this.shortLengthUnit = this.user.units.shortLengths;

    switch (this.user.units.pressure) {
      case 'psi':
        this.pressureValidation = { min: 0, max: 100000 };
        break;
      case 'bar':
        this.pressureValidation = { min: 0, max: 6894 };
        break;
      case 'KPa':
        this.pressureValidation = { min: 0, max: 689475 };
        break;
      case 'atm':
        this.pressureValidation = { min: 0, max: 6804 };
        break;
    }
    this.form.formGroup.controls.wellheadPressure.setValidators([Validators.min(this.pressureValidation.min), Validators.max(this.pressureValidation.max), Validators.required]);

    const density = this.currentString.annularFluid.state.nominalDensity ?? GetValueFromPpg(8.33, unitsLib[this.user.units.density].symbol);

    setTimeout(async () => {
      if (!this.form.formGroup.controls.wellheadPressure.value?.toString()) {
        const tvds = await lastValueFrom(this._trajectoryService.getTvdsFromMds([this.currentString.hangerMd]));
        const defaultHydrostaticPressure = GetMudHydrostaticPressure(tvds[0], density, this.user.units, true);
        const defaultPress = this.isAnnulus
          ? (this.defaultHangerPressure !== 0
              ? this.defaultHangerPressure
              : (defaultHydrostaticPressure !== 0 ? defaultHydrostaticPressure : 0))
          : defaultHydrostaticPressure;

        this.form.formGroup.controls.wellheadPressure.patchValue(defaultPress);
      }
    }, 100);

    this.form.formGroup.get('wellheadPressure')?.valueChanges.subscribe(async value => {
      if (value === null || value === '') {
        const tvds = await lastValueFrom(this._trajectoryService.getTvdsFromMds([this.currentString.hangerMd]));
        const defaultHydrostaticPressure = GetMudHydrostaticPressure(tvds[0], density, this.user.units, true);
        const defaultPress = this.isAnnulus
          ? (this.defaultHangerPressure !== 0
              ? this.defaultHangerPressure
              : (defaultHydrostaticPressure !== 0 ? defaultHydrostaticPressure : 0))
          : defaultHydrostaticPressure;
            this.form.formGroup.controls.wellheadPressure.setValue(defaultPress);
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.defaultRiser.includeRiser && this.form.formGroup.get('includeRiser').value == null) {
      this.form.formGroup.get('includeRiser').setValue(true, { emitEvent: false });
      this.showRiser = true;
      this.checkRiserValues();
    }
  }

  ngOnChanges(): void {
    const weightUnit = unitsLib[this.user.units.linearDensity].symbol;
    const drillPipe = this.drillString['drillPipe'];
    this.shortLengthUnit = this.user.units.shortLengths;
    this.selectedLandingString = drillPipe ?
      `Landing String: OD: ${drillPipe.outsideDiameter.toFixed(2)}${this.shortLengthUnit}, Weight: ${drillPipe.weightPerFoot.toFixed(2)}${weightUnit}, ID: ${drillPipe.insideDiameter.toFixed(2)}${this.shortLengthUnit}, Grade: ${drillPipe.drillPipeGrade?.name || 'Steel'}` :
      'Please Select a Landing String';

    this.landingStringMissing = !drillPipe;
  }

  public hasRiserChange() {
    this.showRiser = !this.showRiser;
    this.checkRiserValues();
  }

  private checkRiserValues() {
    this.form.formGroup.controls.outsideDiameter
      .setValue((this.form.formGroup.value.outsideDiameter ?? GetShortLengthValueFromInches(21, this.shortLengthUnit)), { emitEvent: false });

    this.form.formGroup.controls.insideDiameter
      .setValue((this.form.formGroup.value.insideDiameter ?? GetShortLengthValueFromInches(19.75, this.shortLengthUnit)));
  }
}
